import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Moon } from "lucide-react";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <div className="overflow-hidden">
    <motion.div
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  </div>
);

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const glowOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.2]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden" data-testid="hero-section">
      <motion.div
        style={{ opacity: glowOpacity }}
        className="absolute top-1/3 -right-40 w-[42rem] h-[42rem] rounded-full bg-amber-500/20 blur-[140px] pointer-events-none"
      />
      <div className="absolute -bottom-40 -left-40 w-[30rem] h-[30rem] rounded-full bg-orange-600/10 blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center w-full">
        <div className="lg:col-span-7">
          <MaskedLine delay={0.35}>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-400 mb-8" data-testid="hero-overline">
              Solar deck lights — Pack of 4
            </p>
          </MaskedLine>

          <h1 className="font-serif text-5xl sm:text-6xl lg:text-[5rem] xl:text-[6rem] leading-[0.95] tracking-tight" data-testid="hero-headline">
            <MaskedLine delay={0.5}>Light your space.</MaskedLine>
            <MaskedLine delay={0.65}>
              <span className="italic text-amber-400 text-glow">Power it</span> with
            </MaskedLine>
            <MaskedLine delay={0.8}>the sun.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-md text-stone-400 text-base sm:text-lg leading-relaxed"
            data-testid="hero-subcopy"
          >
            Affordable, stylish solar lighting for balconies, decks, and gardens.
            No wiring. No bills. Just warm light, every night.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.25, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollTo("order")}
              className="rounded-full bg-amber-400 text-stone-950 px-8 py-4 font-medium transition-transform duration-300 hover:scale-105"
              data-testid="hero-cta-button"
            >
              Get the 4-pack — ₹1,499
            </button>
            <button
              onClick={() => scrollTo("how-it-works")}
              className="rounded-full border border-white/15 px-8 py-4 text-stone-300 transition-colors duration-300 hover:border-amber-400/60 hover:text-amber-400"
              data-testid="hero-secondary-button"
            >
              See how it works
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.5 }}
            className="mt-14 flex gap-10 text-sm text-stone-400"
            data-testid="hero-stats"
          >
            {[["8–10h", "warm glow"], ["IP65", "waterproof"], ["1 yr", "warranty"]].map(([v, l]) => (
              <div key={l}>
                <div className="font-serif text-2xl text-stone-50">{v}</div>
                <div className="text-xs uppercase tracking-[0.2em] mt-1">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <motion.div style={{ y: imgY }} className="relative aspect-[4/5] overflow-hidden rounded-2xl clip-frame">
              <img
                src="/images/hero.png"
                alt="LumaDeck solar deck light glowing warm amber"
                className="w-full h-full object-cover animate-float-slow"
                data-testid="hero-product-image"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 1.6 }}
              className="absolute -bottom-5 -left-5 backdrop-blur-xl bg-stone-950/70 border border-white/10 rounded-full px-5 py-3 flex items-center gap-3"
              data-testid="hero-sensor-chip"
            >
              <Moon className="w-4 h-4 text-amber-400" />
              <span className="text-xs uppercase tracking-[0.15em] text-stone-300">Dusk-to-dawn · Auto</span>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-stone-500"
      >
        <ArrowDown className="w-5 h-5 animate-bounce" />
      </motion.div>
    </section>
  );
};
